import type {
  CertificationProgramListItemDTO,
  GetApiCertificationProgramListParams,
} from '@smartsash/api-client';
import type { z } from 'zod';

import { ADMIN_LIST_SEARCH_DEFAULTS, createAdminListSearchSchema } from '@/router/listSearch';
import {
  buildFilterParams,
  collectSearchTerms,
  sortParams,
  type FilterParamMap,
} from '@/utils/listQueryParams';

export const certificationsSearchSchema = createAdminListSearchSchema();

export type CertificationsSearch = z.infer<typeof certificationsSearchSchema>;

/**
 * The builder field for the programme's laboratory.
 *
 * It *is* a column, so the id has to stay the column id `filterColumnsFromText` derives — this
 * constant names it rather than renaming it, so the link helper below and `CERTIFICATIONS_FILTER_PARAMS`
 * cannot drift apart. The column renders `labName`; the parameter behind the field takes `labId`,
 * and `CertificationsFilter` pins the field's options to `{ value: id, label: name }` so a condition
 * carrying an id still reads as a laboratory name in the panel.
 */
export const CERTIFICATIONS_LAB_FIELD = 'laboratory';

/**
 * The builder field for the programme's group. It is not a column — the design shows laboratory
 * and institute, not the group between them — so it is appended as an extra field.
 */
export const CERTIFICATIONS_GROUP_FIELD = 'group';

/**
 * Column id → the endpoint's `sortBy` value.
 *
 * `GET /api/CertificationProgram/list` whitelists exactly these six and answers `400` to anything
 * else, so this map is a closed set rather than a convenience. `passingScore` is deliberately
 * absent from the endpoint's whitelist: if a column for it is ever added it must not be sortable.
 */
export const CERTIFICATIONS_SORT_KEYS: Readonly<Record<string, string>> = {
  laboratory: 'labName',
  institute: 'institutionName',
  validityDays: 'validityDays',
  videos: 'videoCount',
  questions: 'questionCount',
  status: 'status',
};

/**
 * The builder fields that reach `GetApiCertificationProgramListParams`.
 *
 * `status` is the one that repays attention: the endpoint takes a **scalar** enum, not a repeated
 * parameter like the three scope filters around it. Declaring it `multi` would spell the condition
 * as `status=active&status=inactive`, which the endpoint reads as an unknown shape and answers
 * `400` to — losing the whole list rather than one filter. `single` writes one value into the one
 * parameter, which is all the API can express here.
 */
export const CERTIFICATIONS_FILTER_PARAMS: FilterParamMap<GetApiCertificationProgramListParams> = {
  [CERTIFICATIONS_LAB_FIELD]: { kind: 'multi', param: 'labId' },
  institute: { kind: 'multi', param: 'institutionId' },
  [CERTIFICATIONS_GROUP_FIELD]: { kind: 'multi', param: 'groupId' },
  status: { kind: 'single', param: 'status' },
};

/**
 * Turn the URL's `page`/`q`/`sort`/`filters` into `GetApiCertificationProgramListParams`.
 *
 * Shared by the screen's `query` and by `CertificationsFilter`'s cache read: both have to build the
 * identical params object, or the filter panel would read a different cache entry than the table
 * renders from.
 *
 * **Every property emitted here must be a real key of the params type.** This endpoint validates
 * its query string and answers `400` to a parameter it does not recognise rather than ignoring it,
 * so a stray key is a blank screen, not a filter that quietly does nothing. `FilterParamMap` and
 * `sortParams` both constrain their names to `keyof TParams`, which turns that class of mistake
 * into a compile error.
 */
export function certificationsQueryParams(
  search: CertificationsSearch,
): GetApiCertificationProgramListParams {
  const conditions = search.filters ?? [];
  const searchTerms = collectSearchTerms(conditions, search.q ?? '', CERTIFICATIONS_FILTER_PARAMS);
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...sortParams<GetApiCertificationProgramListParams>(search.sort, CERTIFICATIONS_SORT_KEYS),
    ...(searchTerms ? { search: searchTerms } : {}),
    ...buildFilterParams(conditions, CERTIFICATIONS_FILTER_PARAMS),
  };
}

/**
 * The text each column holds for a row — the one source the table, the filter builder's vocabulary
 * and any client-scope axis all read.
 *
 * Unlike Groups, Laboratories and Devices, none of these needs `meta`: the list DTO carries
 * `labName` and `institutionName` on the row itself, so there is no lookup to join and the screen
 * declares no `useTableMeta`.
 *
 * `status` reads the raw API value (`active`/`inactive`) rather than its translated label, because
 * that value is what the `status` query parameter takes. The filter builder shows translated copy
 * through pinned `options`; the table renders its own translated badge in `cell`.
 */
export const certificationColumnText = {
  laboratory: (row: CertificationProgramListItemDTO) => row.labName ?? '',
  institute: (row: CertificationProgramListItemDTO) => row.institutionName ?? '',
  validityDays: (row: CertificationProgramListItemDTO) => String(row.validityDays ?? ''),
  videos: (row: CertificationProgramListItemDTO) => String(row.videoCount ?? ''),
  questions: (row: CertificationProgramListItemDTO) => String(row.questionCount ?? ''),
  status: (row: CertificationProgramListItemDTO) => row.status ?? '',
} as const;

export type CertificationColumnId = keyof typeof certificationColumnText;

/**
 * The search that links a laboratory's "View certifications" action to this list, scoped to that
 * laboratory (HS-1511) — mirrors `devicesFilteredBy`/`laboratoriesFilteredByInstitution`.
 *
 * HS-1162 gave the list a real endpoint and left this link on the bare defaults, so every
 * laboratory's shortcut opened every programme in the system. `labId` was a live parameter even
 * then; what was missing was the decision to narrow, not the capability.
 *
 * A row whose id is absent produces `values: ['']`. `buildFilterParams` drops empty values and then
 * the emptied condition, so the **query** degrades to the unfiltered list this replaces rather than
 * to something the endpoint answers `400` to. The condition itself still travels in `?filters=` and
 * still lights the panel's applied dot, so the user would see a filter indicator over an unfiltered
 * list. Deliberately not short-circuited here: `devicesFilteredBy` and the `usersFilteredBy*` family
 * have the identical property, and guarding one of five would be the drift these helpers exist to
 * prevent. `LabViewDTO.id` is non-nullable on the API, so this is a defensive path, not a live one.
 */
export function certificationsFilteredByLab(labId: string) {
  return {
    ...ADMIN_LIST_SEARCH_DEFAULTS,
    filters: [{ field: CERTIFICATIONS_LAB_FIELD, operator: 'eq' as const, values: [labId] }],
  };
}
